import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import { PriceTag } from "@/components/ui/PriceTag";
import { addWishlistItem, getWishlist, removeWishlistItem, type ProductDetail, type ProductVariant } from "@/lib/api-client";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";
import { useAddToCart } from "@/lib/cart";
import { Gallery } from "./Gallery";
import { ProductTabs } from "./ProductTabs";
import { StockBadge } from "./StockBadge";
import { VariantSelector } from "./VariantSelector";

function pickDefaultVariant(variants: ProductVariant[]): ProductVariant {
  return variants.find((v) => v.isDefault) ?? variants.find((v) => v.stock.inStock) ?? (variants[0] as ProductVariant);
}

/**
 * Certaines valeurs d'attribut seedées (ex. "30 ml") portent déjà l'unité dans
 * leur libellé ; `attr.unit` ("ml") ne doit alors pas être répété, sinon on
 * affiche "30 ml ml".
 */
function formatAttributeValue(attr: { value: string; unit?: string | null }): string {
  if (!attr.unit) return attr.value;
  const alreadyPresent = attr.value.toLowerCase().includes(attr.unit.toLowerCase());
  return alreadyPresent ? attr.value : `${attr.value} ${attr.unit}`;
}

/**
 * Bloc interactif de la fiche produit : titre/galerie/bloc d'achat + onglets.
 * Porté depuis apps/web/components/product/ProductPurchaseArea.tsx —
 * différences forcées par le changement de runtime :
 *
 *  - `addCartItemAction`/`toggleWishlistAction` (Server Actions,
 *    lib/cart-actions.ts / lib/wishlist-actions.ts) → l'ajout panier utilise
 *    `useAddToCart()` (src/lib/cart.ts, le point d'entrée partagé de toute
 *    action panier — synchro cache/badge d'en-tête centralisée là, pas
 *    réimplémentée ici) ; le favori utilise `useMutation()` (TanStack Query)
 *    appelant directement `addWishlistItem()`/`removeWishlistItem()`
 *    (./lib/api-client.ts), `revalidatePath` → `invalidateQueries(["wishlist"])`.
 *  - `isLoggedIn`/`favoriteVariantIds` n'arrivent plus en props depuis la page
 *    (qui n'a plus de session serveur à lire) : ce composant lit lui-même
 *    `useCustomerAuth()` et charge la wishlist via `useQuery()` (activée
 *    seulement si connecté) — simplification volontaire par rapport à la
 *    signature d'origine, la page n'a donc plus besoin de porter cette
 *    plomberie.
 */
export interface ProductPurchaseAreaProps {
  product: ProductDetail;
}

export function ProductPurchaseArea({ product }: ProductPurchaseAreaProps) {
  const { isLoggedIn } = useCustomerAuth();
  const queryClient = useQueryClient();

  const [selectedVariantId, setSelectedVariantId] = useState(() => pickDefaultVariant(product.variants).id);
  const [quantity, setQuantity] = useState(1);
  const [feedback, setFeedback] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const selectedVariant = useMemo(
    () => product.variants.find((v) => v.id === selectedVariantId) ?? pickDefaultVariant(product.variants),
    [product.variants, selectedVariantId],
  );

  const wishlistQuery = useQuery({
    queryKey: ["wishlist"],
    queryFn: () => getWishlist(),
    enabled: isLoggedIn,
  });
  const favoriteIds = useMemo(
    () => new Set((wishlistQuery.data ?? []).map((item) => item.variantId)),
    [wishlistQuery.data],
  );
  const isFavorite = favoriteIds.has(selectedVariant.id);

  const maxQuantity = selectedVariant.stock.inStock
    ? Math.max(1, Math.min(selectedVariant.stock.quantityAvailable, 99))
    : 1;

  function handleSelectVariant(variantId: string) {
    setSelectedVariantId(variantId);
    setQuantity(1);
    setFeedback(null);
  }

  const addToCartMutation = useAddToCart();

  const toggleWishlistMutation = useMutation({
    mutationFn: async (variantId: string) => {
      const wasFavorite = favoriteIds.has(variantId);
      if (wasFavorite) await removeWishlistItem(variantId);
      else await addWishlistItem(variantId);
      return { wasFavorite };
    },
    onSuccess: ({ wasFavorite }) => {
      void queryClient.invalidateQueries({ queryKey: ["wishlist"] });
      setFeedback({
        type: "success",
        message: wasFavorite ? "Retiré de vos favoris." : "Ajouté à vos favoris.",
      });
    },
    onError: () => {
      setFeedback({ type: "error", message: "Une erreur est survenue. Merci de réessayer." });
    },
  });

  function handleAddToCart() {
    setFeedback(null);
    addToCartMutation.mutate(
      { variantId: selectedVariant.id, quantity },
      {
        onSuccess: () => setFeedback({ type: "success", message: "Ajouté au panier." }),
        onError: () =>
          setFeedback({ type: "error", message: "Impossible d'ajouter ce produit au panier. Réessayez." }),
      },
    );
  }

  function handleToggleWishlist() {
    setFeedback(null);
    toggleWishlistMutation.mutate(selectedVariant.id);
  }

  const compareAtPrice = selectedVariant.compareAtPrice;
  const discountPercent =
    compareAtPrice != null && compareAtPrice.amountMinor > selectedVariant.price.amountMinor
      ? Math.round(((compareAtPrice.amountMinor - selectedVariant.price.amountMinor) / compareAtPrice.amountMinor) * 100)
      : null;

  const keyPoints = selectedVariant.attributes.slice(0, 5);

  const tabs = [
    {
      id: "description",
      label: "Description",
      content: (
        <p className="whitespace-pre-line font-body text-sm text-ink">
          {product.descriptionLong ?? product.descriptionShort ?? "Aucune description disponible pour ce produit."}
        </p>
      ),
    },
    {
      id: "caracteristiques",
      label: "Caractéristiques",
      content:
        selectedVariant.attributes.length > 0 ? (
          <table className="w-full border-collapse font-body text-sm">
            <tbody>
              {selectedVariant.attributes.map((attr) => (
                <tr key={attr.code} className="border-b border-line">
                  <th scope="row" className="py-2 pr-4 text-left font-medium text-muted">
                    {attr.label}
                  </th>
                  <td className="py-2 text-ink">{formatAttributeValue(attr)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="font-body text-sm text-muted">Aucune caractéristique structurée pour cette variante.</p>
        ),
    },
    {
      id: "utilisation",
      label: "Utilisation",
      content:
        product.usageInstructions || product.precautions ? (
          <div className="space-y-4 font-body text-sm text-ink">
            {product.usageInstructions ? <p className="whitespace-pre-line">{product.usageInstructions}</p> : null}
            {product.precautions ? (
              <p className="whitespace-pre-line text-muted">
                <strong className="text-ink">Précautions : </strong>
                {product.precautions}
              </p>
            ) : null}
          </div>
        ) : (
          <p className="font-body text-sm text-muted">Aucune information d&apos;utilisation renseignée.</p>
        ),
    },
    {
      id: "avis",
      label: `Avis (${product.rating.count})`,
      content:
        product.rating.count > 0 ? (
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="shrink-0 text-center">
              <p className="font-display text-3xl font-bold text-navy">{product.rating.average.toFixed(1)} / 5</p>
              <p aria-hidden="true" className="text-gold">
                {"★".repeat(Math.round(product.rating.average))}
                {"☆".repeat(5 - Math.round(product.rating.average))}
              </p>
              <p className="font-body text-xs text-muted">{product.rating.count} avis</p>
            </div>
            <div className="flex-1 space-y-1">
              {([5, 4, 3, 2, 1] as const).map((star) => {
                const count = product.rating.distribution[String(star) as "1" | "2" | "3" | "4" | "5"];
                const pct = product.rating.count > 0 ? Math.round((count / product.rating.count) * 100) : 0;
                return (
                  <div key={star} className="flex items-center gap-2 font-body text-xs text-muted">
                    <span className="w-8 shrink-0">{star}★</span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-line">
                      <span className="block h-full bg-gold" style={{ width: `${pct}%` }} />
                    </span>
                    <span className="w-6 shrink-0 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <p className="font-body text-sm text-muted">Aucun avis pour ce produit pour le moment.</p>
        ),
    },
  ];

  return (
    <div>
      <div className="flex flex-col gap-6 lg:grid lg:grid-cols-12 lg:gap-x-10">
        {/* Titre — au-dessus de la galerie sur mobile (§4.6), en haut de la colonne droite sur bureau (§4.1). */}
        <div className="order-1 lg:order-none lg:col-span-6 lg:col-start-7 lg:row-start-1">
          {product.brand ? (
            <Link
              to={`/marques/${product.brand.slug}`}
              className="font-body text-xs font-semibold uppercase tracking-wide text-muted hover:text-navy"
            >
              {product.brand.name}
            </Link>
          ) : null}
          <h1 className="mt-1 font-display text-2xl font-bold text-navy sm:text-3xl">{product.name}</h1>
          {product.subtitle ? <p className="mt-1 font-body text-sm text-muted">{product.subtitle}</p> : null}
          {product.rating.count > 0 ? (
            <a href="#avis" className="mt-2 inline-flex items-center gap-1 font-body text-sm text-muted hover:text-navy">
              <span aria-hidden="true" className="text-gold">
                {"★".repeat(Math.round(product.rating.average))}
                {"☆".repeat(5 - Math.round(product.rating.average))}
              </span>
              {product.rating.average.toFixed(1)} — {product.rating.count} avis
            </a>
          ) : null}
        </div>

        {/* Galerie — colonne gauche entière sur bureau. */}
        <div className="order-2 lg:order-none lg:col-span-6 lg:col-start-1 lg:row-span-2 lg:row-start-1">
          <Gallery media={product.media} productName={product.name} />
        </div>

        {/* Prix + description courte + points clés + variantes + stock + ajout. */}
        <div className="order-3 lg:order-none lg:col-span-6 lg:col-start-7 lg:row-start-2 lg:self-start">
          <div className="border-t border-line pt-4">
            <div className="flex items-baseline gap-2">
              <PriceTag
                amountMinor={selectedVariant.price.amountMinor}
                compareAtAmountMinor={selectedVariant.compareAtPrice?.amountMinor ?? null}
              />
              {discountPercent ? (
                <span className="font-body text-sm font-semibold text-golddark">-{discountPercent}%</span>
              ) : null}
            </div>
          </div>

          {product.descriptionShort ? (
            <p className="mt-4 whitespace-pre-line font-body text-sm text-ink">{product.descriptionShort}</p>
          ) : null}

          {keyPoints.length > 0 ? (
            <ul className="mt-4 space-y-1.5">
              {keyPoints.map((attr) => (
                <li key={attr.code} className="flex items-start gap-2 font-body text-sm text-ink">
                  <span aria-hidden="true" className="mt-0.5 text-gold">
                    {"✓"}
                  </span>
                  <span>
                    {attr.label} : {formatAttributeValue(attr)}
                  </span>
                </li>
              ))}
            </ul>
          ) : null}

          <div className="mt-5 border-t border-line pt-4">
            <VariantSelector
              variants={product.variants}
              selectedVariantId={selectedVariant.id}
              onSelect={handleSelectVariant}
            />
          </div>

          <div className="mt-4">
            <StockBadge stock={selectedVariant.stock} />
          </div>

          <div className="mt-4 flex items-center gap-3">
            {selectedVariant.stock.inStock ? (
              <>
                <div className="flex items-center rounded-sm border border-line">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    disabled={quantity <= 1}
                    aria-label="Diminuer la quantité"
                    className="px-3 py-2 text-navy disabled:opacity-40"
                  >
                    −
                  </button>
                  <span className="min-w-[2ch] px-2 text-center font-body text-sm">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(maxQuantity, q + 1))}
                    disabled={quantity >= maxQuantity}
                    aria-label="Augmenter la quantité"
                    className="px-3 py-2 text-navy disabled:opacity-40"
                  >
                    +
                  </button>
                </div>
                <Button onClick={handleAddToCart} disabled={addToCartMutation.isPending} className="flex-1">
                  {addToCartMutation.isPending ? "Ajout…" : "Ajouter au panier"}
                </Button>
              </>
            ) : (
              <div
                role="status"
                className="flex flex-1 items-center justify-center rounded-sm bg-red-600 px-6 py-3 font-body text-sm font-medium uppercase tracking-wide text-white"
              >
                Indisponible
              </div>
            )}

            {isLoggedIn ? (
              <button
                type="button"
                onClick={handleToggleWishlist}
                disabled={toggleWishlistMutation.isPending}
                aria-pressed={isFavorite}
                aria-label={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                title={isFavorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                className={[
                  "flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border text-lg transition-colors disabled:opacity-50",
                  isFavorite ? "border-gold bg-gold/10 text-gold2" : "border-line text-muted hover:border-navy hover:text-navy",
                ].join(" ")}
              >
                <span aria-hidden="true">{isFavorite ? "♥" : "♡"}</span>
              </button>
            ) : (
              <Link
                to="/connexion"
                aria-label="Se connecter pour ajouter aux favoris"
                title="Se connecter pour ajouter aux favoris"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-line text-lg text-muted hover:border-navy hover:text-navy"
              >
                <span aria-hidden="true">♡</span>
              </Link>
            )}
          </div>

          {feedback ? (
            <p
              role="status"
              className={["mt-2 font-body text-sm", feedback.type === "success" ? "text-navy" : "text-red-600"].join(" ")}
            >
              {feedback.message}
            </p>
          ) : null}

          {/* Réassurance : promesses génériques — le calcul dynamique (jour, heure de coupure)
              décrit en §4.2 dépendrait de `setting`/`shipping_method`, hors périmètre des
              fonctions catalogue de cette mission. */}
          <ul className="mt-6 space-y-2 border-t border-line pt-4 font-body text-sm text-muted">
            <li className="flex items-center gap-2">
              <span aria-hidden="true">{"\u{1F69A}"}</span> Livraison rapide à Abidjan et dans les grandes villes
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true">{"\u{1F4B5}"}</span> Paiement à la livraison ou par mobile money
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true">{"↩"}</span> Retours acceptés sous conditions
            </li>
            <li className="flex items-center gap-2">
              <span aria-hidden="true">{"\u{1F4AC}"}</span>{" "}
              <Link to="/contact" className="underline hover:text-navy">
                Une question ? Contactez-nous
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div id="avis" className="mt-12 border-t border-line pt-8">
        <ProductTabs tabs={tabs} />
      </div>
    </div>
  );
}
